import {
  Badge,
  BadgeVariantEnum,
  Checkbox,
  ChevronDownIcon,
  Column,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnumOld,
  IconSizeEnum,
  matchString,
  TableOptions,
  WarningInfoIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TableRow } from '@/components/TableRow';
import {
  ReliabilityInlineBadge,
  useBroadcastScheduleContext,
} from '@/components/VideoPresencesInlineControls';
import {
  collectAllIds,
  getChildrenIds,
  groupPresences,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import { RowExpanderSync } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/hooks/useRowExpanderSync';
import {
  FrameActionType,
  GROUP_OPTION_VALUES,
  PostPresenceFor,
  TBroadcastScheduleOnUpdateInline,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import {
  isSegmentLogo,
  sortGroupPresences,
} from '@/features/broadcast-schedule-program/helpers/sort-by-key';
import { useGetLabels } from '@/features/labels/api/get-labels';
import { useLogosLabelsFilterStore } from '@/features/logos/stores/logos-labels-filter';
import { useGetSupports } from '@/features/programs/api/get-supports';
import { TSegmentLogo, TSegmentPresence } from '@/features/segments/types';
import { getOffsetMs, SpritesheetThumbnail } from '@/features/video-thumbnails';
import { useThumbnailContextResolver } from '@/features/video-thumbnails/hooks/use-thumbnail-context-resolver';
import { useGroupedPresences } from '@/hooks/use-grouped-presences';
import { PresenceTypeEnum, TPresenceItem } from '@/types/presences';

type useVidePresencesDatatableProps<T extends TPresenceItem> = {
  items: T[];
  onPreview?: (item: T, frame?: FrameActionType) => void;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<PostPresenceFor<T>>;
  onUpdate?: (item: T) => void;
  onDelete?: (item: T, silently?: boolean) => void;
  // Optional props for People support (with defaults for backward compat)
  entityIdKey?: keyof T;
  showSupportColumn?: boolean;
  renderEntityCell?: (item: T, showSubArrows: (f: (v: boolean) => boolean) => void) => ReactNode;
  buildDataToSave?: (item: T) => PostPresenceFor<T>;
  presenceData?: { id?: string };
  searchBarPlaceholder?: string;
  defaultExpandedGroups?: boolean;
};

export function useVideoPresencesDatatable<T extends TPresenceItem>({
  items,
  onPreview,
  entityIdKey,
  showSupportColumn = true,
  renderEntityCell,
  searchBarPlaceholder,
  defaultExpandedGroups = false,
}: useVidePresencesDatatableProps<T>) {
  const { t } = useTranslation();
  const { data: allSupports = [] } = useGetSupports();
  const { presenceData: activePresenceData } = useBroadcastScheduleProgram();
  const resolveThumbnailContext = useThumbnailContextResolver(
    items as Array<{ segment_id?: string | null }>
  );

  const { data: allLabels = [] } = useGetLabels();
  const { totalPresences, indexLogos, indexPeople, logosById, peopleById } = useGroupedPresences(
    items,
    onPreview,
    entityIdKey as any
  );
  const resolvedEntities = useMemo(() => ({ logosById, peopleById }), [logosById, peopleById]);
  const {
    selectedPresencesIds,
    setGroupedPresences,
    setSelectedPresencesIds,
    hasSelectedPresences,
    groupPresenceCriteria,
    getItemParentTitle,
    groupedPresences,
    expandAllPresences,
    setExpandAllPresences,
    sortDirection,
    thumbnailSize,
    tableFiltersRef,
    setRowExpandOverride: setOverride,
    getRowExpandDesired: getDesired,
  } = useBroadcastScheduleContext();
  const getItemParentTitleRef = useRef(getItemParentTitle);
  getItemParentTitleRef.current = getItemParentTitle;
  const resolvedEntitiesRef = useRef(resolvedEntities);
  resolvedEntitiesRef.current = resolvedEntities;

  useEffect(() => {
    if (defaultExpandedGroups) {
      setExpandAllPresences(true);
    }
  }, []);

  function buildColumns(tableType: PresenceTypeEnum): Column<T>[] {
    const base: Column<T>[] = [
      {
        id: 'check',
        title: '',
        className: '!pr-size-2',
        width: '5%',
        renderTitle: items => {
          const itemsIds = collectAllIds(items as TSegmentLogo[]);
          const isChecked =
            itemsIds.length > 0 &&
            hasSelectedPresences &&
            itemsIds.every(itemId => itemId && selectedPresencesIds.includes(itemId));

          const isIndeterminate = hasSelectedPresences && !isChecked;

          return (
            <div className="ml-1 flex h-full items-center">
              <Checkbox
                checked={isChecked || isIndeterminate}
                indeterminate={isIndeterminate}
                onChange={() => {
                  if (isChecked) {
                    setSelectedPresencesIds(prev => prev.filter(id => !itemsIds.includes(id)));
                  } else {
                    const itemsToSet = Array.from(new Set([...selectedPresencesIds, ...itemsIds]));
                    setSelectedPresencesIds(itemsToSet);
                  }
                }}
              />
            </div>
          );
        },
        isVisible: true,
        singleColumnTitleForMobile: 'Seleccionar todos',
        render: item => {
          if (!item.id) {
            return;
          }

          const itemId = item.id;
          const childrenIds = getChildrenIds(groupedPresences as any, itemId);
          const currentGroupedIds = childrenIds.length > 0 ? childrenIds : [itemId];

          const alreadySet = currentGroupedIds.every(
            itemId => itemId && selectedPresencesIds.includes(itemId)
          );

          const isIndeterminate =
            currentGroupedIds.some(itemId => itemId && selectedPresencesIds.includes(itemId)) &&
            !alreadySet;

          return (
            <div className="ml-1">
              <Checkbox
                checked={alreadySet}
                indeterminate={isIndeterminate}
                onChange={() => {
                  if (alreadySet) {
                    setSelectedPresencesIds(prev =>
                      prev.filter(id => !currentGroupedIds.includes(id))
                    );
                  } else {
                    const itemsToSet = Array.from(
                      new Set([...selectedPresencesIds, ...currentGroupedIds])
                    );
                    setSelectedPresencesIds(itemsToSet);
                  }
                }}
              />
            </div>
          );
        },
      },
      {
        id: 'logo',
        title: t('Presencia'),
        width: `150px`,
        className: '!pr-size-2 !pl-0',
        searchable: true,
        matchFn: (query, item) => {
          let label = item?.entity_label;
          if (!label && (item as any)?.children?.length) {
            label = (item as any)?.children[0].entity_label;
          }

          return matchString((label ?? '').toString(), query);
        },
        render: (item, _index, _rows, showSubArrows) =>
          renderEntityCell?.(item, showSubArrows) ?? null,
      },
      {
        id: 'timelineStart',
        title: t('Inicio'),
        width: `125px`,
        className: '!pr-size-2',
        render: item => (
          <div className="flex items-center gap-1">
            <TableRow
              title={getItemParentTitleRef.current(item as any, resolvedEntitiesRef.current)}
              className="flex flex-col"
            />
          </div>
        ),
      },
      {
        id: 'timelineEnd',
        title: t('Fin'),
        width: `125px`,
        className: '!pr-size-2',
        render: item => {
          const count = (item as any).children?.length ?? 0;
          return <Badge variant={BadgeVariantEnum.DefaultFilled}>{count}</Badge>;
        },
      },
      {
        id: 'visibility',
        title: t('Visibilidad'),
        width: `120px`,
        className: '!pr-size-2',
        render: () => <></>,
      },
      {
        id: 'fiable',
        title: t('Fiabilidad'),
        width: `120px`,
        className: '!pr-size-2',
        render: item => (
          <div className="flex flex-col items-center gap-size-4">
            <div className="flex flex-col items-center gap-2 pr-[30px]">
              {(item as any).children?.some((child: any) => child.entity_score < 0.4) ? (
                <WarningInfoIcon className="text-feedback-error-20" />
              ) : null}
              <ReliabilityInlineBadge
                className="w-[73px] justify-center text-xs"
                isManual={(item as any).parameters?.is_manual || false}
              />
            </div>
          </div>
        ),
      },
      {
        id: 'actions',
        title: t('Acciones'),
        width: '115px',
        className: 'items-end !pr-size-2',
        render: (item, _index, _rows, showSubArrows, subArrowsAreVisible) => {
          const rowId = item.id || '';

          return (
            <>
              <RowExpanderSync
                rowId={rowId}
                expandAll={expandAllPresences}
                getDesired={getDesired}
                subArrowsAreVisible={subArrowsAreVisible}
                showSubArrows={showSubArrows}
              />
              <div className="inline-flex w-full justify-end pr-2">
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton
                  color={IconButtonColorEnum.NONE_DEFAULT}
                  shape={IconButtonShapeEnumOld.OLD_SQUARED}
                  onClick={() => {
                    const next = !subArrowsAreVisible;
                    setOverride(rowId, next);
                    showSubArrows(next);
                  }}
                >
                  <ChevronDownIcon
                    size={IconSizeEnum.XS}
                    className={clsx(
                      'duration-300 ease-in-out',
                      subArrowsAreVisible && '-rotate-180'
                    )}
                  />
                </IconButton>
              </div>
            </>
          );
        },
      },
    ];

    if (tableType === PresenceTypeEnum.PEOPLE) {
      return base;
    }

    const supportColumn: Column<T> = {
      id: 'support',
      title: t('Soporte'),
      width: `${90 / 6}%`,
      className: 'pr-size-2',
      render: () => <></>,
    };
    const index = 5;

    return [...base.slice(0, index), supportColumn, ...base.slice(index)] as Column<T>[];
  }

  type FilterChangeData = Parameters<NonNullable<TableOptions['filters']>[number]['onChange']>[0];

  const buildOptions = (tableType: PresenceTypeEnum): Partial<TableOptions> => {
    const labelsFilter = {
      name: 'labels',
      placeholder: 'Etiquetas',
      width: 'w-52',
      isMultiple: true,
      initialValue: tableFiltersRef.current['labels'],
      options: allLabels
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ name, id }) => ({ label: name, value: id })),
      onChange: (data: FilterChangeData) => {
        tableFiltersRef.current = { ...tableFiltersRef.current, labels: data.value };
        const selectedLabels = Array.isArray(data.value)
          ? (data.value as string[])
          : data.value
            ? [data.value]
            : [];
        // Single source of truth (persists to localStorage + keeps the toolkit badge in sync).
        useLogosLabelsFilterStore.getState().setLabels(selectedLabels);
        const resolvedEntityIdKey = entityIdKey ?? ('logo_id' as keyof T);
        let sortedPresences = sortGroupPresences(
          groupPresences(
            items as TSegmentPresence[],
            groupPresenceCriteria,
            resolvedEntityIdKey as any
          ),
          undefined,
          sortDirection
        );
        if (data.value.length > 0) {
          sortedPresences = sortedPresences.filter(item => {
            const presencesToCheck = (item as any).children ?? [item];
            return presencesToCheck.some((presence: any) => {
              if (isSegmentLogo(presence)) {
                // A presence whose logo is not resolved yet stays shown rather than hidden on
                // a guess.
                const logo = logosById.get(presence.logo_id);
                if (!logo) return true;
                return logo.labels?.some((label_id: string) => data.value.includes(label_id));
              }
              // Featured people do not have labels
              return false;
            });
          });
        }
        setGroupedPresences(prev => {
          const isEqual = JSON.stringify(prev) === JSON.stringify(sortedPresences);
          return isEqual ? prev : (sortedPresences as any);
        });

        return sortedPresences;
      },
    };

    const fiableFilter = {
      name: 'fiable',
      placeholder: t('Grado de fiabilidad'),
      options: [] as { label: string; value: string }[],
      initialValue: tableFiltersRef.current['fiable'],
      onChange: (data: FilterChangeData) => {
        tableFiltersRef.current = { ...tableFiltersRef.current, fiable: data.value };
        return groupedPresences;
      },
    };

    const filtersByType: Partial<Record<PresenceTypeEnum, (typeof fiableFilter)[]>> = {
      [PresenceTypeEnum.LOGOS]: [labelsFilter, fiableFilter],
      [PresenceTypeEnum.PEOPLE]: [fiableFilter],
    };

    return {
      initialSortBy: { id: 'updated_at', order: 'desc' },
      filters: filtersByType[tableType] ?? [],
      multiselect: {
        text: 'Multiselección',
        disableClick: true,
        isShowOptions: true,
        counter: { selected: selectedPresencesIds.length, total: totalPresences },
        actions: [],
      },
      searchBarPlaceholder: searchBarPlaceholder ?? t('Buscar por logo'),
    };
  };

  const columnsDeps = useMemo(
    () => [
      t,
      groupedPresences,
      logosById,
      allSupports,
      selectedPresencesIds,
      groupPresenceCriteria,
      expandAllPresences,
      showSupportColumn,
      renderEntityCell,
    ],
    [
      t,
      groupedPresences,
      logosById,
      allSupports,
      selectedPresencesIds,
      groupPresenceCriteria,
      expandAllPresences,
      showSupportColumn,
      renderEntityCell,
    ]
  );

  function buildThumbsColumns(): Column<T>[] {
    return [
      {
        id: 'thumbnails',
        title: '',
        width: '100%',
        isVisible: true,
        render: item => {
          const leafIds = collectAllIds([item as any]);

          const allSelected =
            leafIds.length > 0 && leafIds.every(id => selectedPresencesIds.includes(id));
          const someSelected = leafIds.some(id => selectedPresencesIds.includes(id));

          const resolveAlt = (src: any) =>
            logosById.get(src.logo_id)?.name ?? src.entity_label ?? '';

          const resolvePresenceUrl = (src: any): string | undefined =>
            (indexLogos as any)[src.logo_id]?.parameters?.main_logo_presigned_url ??
            (indexPeople as any)[src.people_id]?.parameters?.main_logo_presigned_url;

          const images: { id: string; alt: string; source: T; presenceUrl?: string }[] = (
            (item as any).children as any[]
          ).map((child: any) => ({
            id: child.id ?? child.logo_id,
            alt: resolveAlt(child),
            source: child,
            presenceUrl: resolvePresenceUrl(child),
          }));

          const headerSrc = (item as any).children?.[0];
          const headerImageUrl =
            groupPresenceCriteria === GROUP_OPTION_VALUES.Presence
              ? (resolvePresenceUrl(headerSrc) ?? '/default-logo.svg')
              : '/default-logo.svg';

          const headerTitle = (() => {
            if (groupPresenceCriteria === GROUP_OPTION_VALUES.Presence && headerSrc?.people_id) {
              const person = (indexPeople as any)[headerSrc.people_id];
              return (
                [person?.name, person?.surname].filter(Boolean).join(' ') ||
                headerSrc.entity_label ||
                ''
              );
            }
            return getItemParentTitle(item as any, resolvedEntities);
          })();

          return (
            <div>
              <div className="flex items-center gap-2 border-b px-2 py-1">
                <Checkbox
                  checked={allSelected || someSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={() => {
                    if (allSelected) {
                      setSelectedPresencesIds(prev => prev.filter(id => !leafIds.includes(id)));
                    } else {
                      setSelectedPresencesIds(prev => Array.from(new Set([...prev, ...leafIds])));
                    }
                  }}
                />
                <img src={headerImageUrl} alt="" className="h-10 w-20 rounded object-contain" />
                <span className="flex-1 text-sm font-medium">{headerTitle}</span>
                <Badge variant={BadgeVariantEnum.DefaultFilled}>{leafIds.length}</Badge>
              </div>
              <div className="flex flex-wrap gap-2 p-2">
                {images.map(({ id, alt, source, presenceUrl }) => {
                  const isActive = activePresenceData?.id === id;
                  const isSelected = selectedPresencesIds.includes(id);
                  const { thumbnails: childThumbnails, referenceStartDatetime: childReference } =
                    resolveThumbnailContext(source as { segment_id?: string | null });
                  return (
                    <div key={id} className="group relative">
                      <SpritesheetThumbnail
                        startOffsetMs={getOffsetMs((source as any).start_datetime, childReference)}
                        endOffsetMs={getOffsetMs((source as any).end_datetime, childReference)}
                        thumbnails={childThumbnails}
                        bbox={(source as any).parameters?.bbox}
                        displayWidth={thumbnailSize}
                        alt={alt}
                        presenceImageUrl={presenceUrl}
                        className={clsx(
                          'cursor-pointer',
                          (isActive || isSelected) && 'ring-[3px] ring-blue-500'
                        )}
                        onClick={() => onPreview?.(source as T)}
                      />
                      <div
                        className={clsx(
                          'absolute left-1 top-1',
                          isSelected ? 'flex' : 'hidden group-hover:flex'
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => {
                            setSelectedPresencesIds(prev =>
                              prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                            );
                            onPreview?.(source as T);
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        },
      },
    ];
  }

  const getColumns: Column<T>[] = useMemo(() => buildColumns(PresenceTypeEnum.LOGOS), columnsDeps);

  const getLogosThumbsColumns: Column<T>[] = useMemo(
    () => buildThumbsColumns(),
    [
      thumbnailSize,
      groupPresenceCriteria,
      getItemParentTitle,
      resolvedEntities,
      selectedPresencesIds,
      activePresenceData,
      resolveThumbnailContext,
    ]
  );

  const getPeopleColumns: Column<T>[] = useMemo(
    () => buildColumns(PresenceTypeEnum.PEOPLE),
    columnsDeps
  );

  const optionsDeps = [
    t,
    allLabels,
    groupedPresences,
    selectedPresencesIds,
    hasSelectedPresences,
    totalPresences,
    items,
    groupPresenceCriteria,
    searchBarPlaceholder,
    entityIdKey,
    sortDirection,
  ] as const;

  const getOptions: Partial<TableOptions> = useMemo(
    () => buildOptions(PresenceTypeEnum.LOGOS),

    [...optionsDeps]
  );

  const getPeopleOptions: Partial<TableOptions> = useMemo(
    () => buildOptions(PresenceTypeEnum.PEOPLE),

    [...optionsDeps]
  );

  return {
    getColumns,
    getLogosThumbsColumns,
    getPeopleColumns,
    getOptions,
    getPeopleOptions,
  };
}
